import React, { useState, useEffect } from 'react';
import './GetQuote.css';

import { FaUser, FaEnvelope, FaPhone, FaLayerGroup, FaCubes, FaRulerCombined, FaMapMarkerAlt, FaCalendar, FaClock, FaComment, FaAngleDown } from 'react-icons/fa';
import axios from "axios";
import { Heading } from '../../../components';

const API_PATH = 'https://floorcraft-react.asdesignsgalaxy.com/api/phpmailer/quote.php';

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const GetQuote = () => {

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    flooringType: '',
    materialQuality: '',
    sqFootage: '',
    address: '',
    date: '',
    time: '',
    comment: '',
    installation: false,
    removal: false,
    underlayment: false
  });

  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];
      
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.flooringType) validationErrors.push('Flooring type is required');
      if (!formData.materialQuality) validationErrors.push('Material quality is required');
      if (!formData.sqFootage) validationErrors.push('Square footage is required');
      if (!formData.address) validationErrors.push('Address is required');
      if (!formData.date) validationErrors.push('Date is required');
      if (!formData.time) validationErrors.push('Time is required');
      if (!formData.comment) validationErrors.push('Message is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        // For checkboxes, convert boolean to string
        if (typeof value === 'boolean') {
          postData.append(key, value ? 'true' : 'false');
        } else {
          postData.append(key, value);
        }
      });

      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        flooringType: '',
        materialQuality: '',
        sqFootage: '',
        address: '',
        date: '',
        time: '',
        comment: '',
        installation: false,
        removal: false,
        underlayment: false
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  return (
    <section className="custom-form get-quote">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={"Request A Quote"} subTitle={"Custom Flooring Solutions"} linear sideHeading />

            <div className="input-box">
              {/* Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">
              {/* Phone Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="tel"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Flooring Type Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="flooringType"><FaLayerGroup /></label>
                  <select
                    className="select-box"
                    id="flooringType"
                    name="flooringType"
                    value={formData.flooringType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Flooring Type</option>
                    <option value="hardwood">Hardwood</option>
                    <option value="laminate">Laminate</option>
                    <option value="vinyl">Vinyl</option>
                    <option value="tile">Tile</option>
                    <option value="carpet">Carpet</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>
            </div>

            <div className="input-box">
              {/* Material Quality Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="materialQuality"><FaCubes /></label>
                  <select
                    className="select-box"
                    id="materialQuality"
                    name="materialQuality"
                    value={formData.materialQuality}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Material Quality</option>
                    <option value="basic">Basic</option>
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              {/* Square Footage Input Field */}
              <div className="input-field">
                <label htmlFor="sqFootage"><FaRulerCombined /></label>
                <input
                  type="number"
                  name="sqFootage"
                  className="box"
                  id="sqFootage"
                  placeholder="Square Footage"
                  value={formData.sqFootage}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">
              {/* Address Input Field */}
              <div className="input-field">
                <label htmlFor="address"><FaMapMarkerAlt /></label>
                <input
                  type="text"
                  name="address"
                  className="box"
                  id="address"
                  placeholder="Address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">
              {/* Date Input Field */}
              <div className="input-field">
                <label htmlFor="date"><FaCalendar /></label>
                <input
                  type="text"
                  name="date"
                  placeholder="Date"
                  id="date"
                  className="box"
                  onFocus={(e) => e.target.type = 'date'}
                  onBlur={(e) => e.target.type = 'text'}
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
  
              {/* Time Input Field */}
              <div className="input-field">
                <label htmlFor="time"><FaClock /></label>
                <input
                  type="text"
                  name="time"
                  placeholder="Time"
                  id="time"
                  className="box"
                  onFocus={(e) => e.target.type = 'time'}
                  onBlur={(e) => e.target.type = 'text'}
                  value={formData.time}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Comment Text Area */}
            <div className="input-field text-area">
              <label htmlFor="comment"><FaComment /></label>
              <textarea
                cols="30"
                rows="10"
                name="comment"
                className="box"
                id="comment"
                placeholder="Message"
                value={formData.comment}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Extra Services Checkboxes */}
            <div className="extra-services">
              {/* Installation Checkbox */}
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="installation-checkbox"
                  name="installation"
                  checked={formData.installation}
                  onChange={handleCheckboxChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="installation-checkbox">Installation</label>
                <div className="checkbox-indicator"></div>
              </div>
          
              {/* Removal of Old Checkbox */}
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="removal-checkbox"
                  name="removal"
                  checked={formData.removal}
                  onChange={handleCheckboxChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="removal-checkbox">Removal of Old</label>
                <div className="checkbox-indicator"></div>
              </div>
          
              {/* Underlayment Checkbox */}
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="underlayment-checkbox"
                  name="underlayment"
                  checked={formData.underlayment}
                  onChange={handleCheckboxChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="underlayment-checkbox">Underlayment</label>
                <div className="checkbox-indicator"></div>
              </div>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {/* Error and Success Messages */}
            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}
            {mailSent && <div className="alert success"><span>Quote Request Submitted Successfully</span></div>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default GetQuote;